import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'success' | 'error';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  loading?: boolean;
  noFocus?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  loading = false,
  noFocus = false,
  ...props
}) => {
  const baseClasses = noFocus 
    ? 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed'
    : 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variantClasses = {
    primary: 'text-black hover:bg-opacity-90 focus:ring-gray-400 backdrop-blur-sm border border-gray-300 bg-[#ffffffb4]',
    secondary: 'text-white hover:bg-opacity-90 focus:ring-gray-600 backdrop-blur-sm border border-gray-600 bg-[#1a1a1ab4]',
    outline: 'border-2 text-black hover:bg-opacity-20 focus:ring-gray-400 backdrop-blur-sm border-[#ffffffb4] hover:bg-[#ffffffb4]',
    ghost: 'text-gray-700 hover:bg-opacity-60 focus:ring-gray-400 backdrop-blur-sm hover:bg-[#ffffffb4]',
    success: 'text-white hover:bg-opacity-90 focus:ring-green-400 backdrop-blur-sm border border-green-600 bg-green-600',
    error: 'text-white hover:bg-opacity-90 focus:ring-red-400 backdrop-blur-sm border border-red-600 bg-red-600'
  };
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};
